import type { ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { RequireAuth } from '@/lib/auth/RequireAuth';
import { LoginPage } from '@/pages/LoginPage';
import { JoinPage } from '@/pages/JoinPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';
import { HomePage } from '@/pages/HomePage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { NewSubmissionPage } from '@/pages/NewSubmissionPage';
import { ImportPage } from '@/pages/ImportPage';
import { SubmissionDetailPage } from '@/pages/SubmissionDetailPage';
import { UploadDocsPage } from '@/pages/UploadDocsPage';
import { ValidationReportPage } from '@/pages/ValidationReportPage';
import { TemplatesListPage } from '@/pages/TemplatesListPage';
import { TemplateDetailPage } from '@/pages/TemplateDetailPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { RolesPage } from '@/pages/admin/RolesPage';
import { AuditLogPage } from '@/pages/admin/AuditLogPage';
import { AssistantActivityPage } from '@/pages/admin/AssistantActivityPage';
import { CompanyProfilePage } from '@/pages/admin/CompanyProfilePage';
import { ValidationRulesPage } from '@/pages/admin/ValidationRulesPage';
import { FormTemplatesPage } from '@/pages/admin/FormTemplatesPage';
import { PromptLibraryPage } from '@/pages/admin/PromptLibraryPage';
import { SpecFilesPage } from '@/pages/admin/SpecFilesPage';
import { SettingsPage } from '@/pages/admin/SettingsPage';
import { SystemHealthPage } from '@/pages/admin/SystemHealthPage';
import { CompilerPage } from '@/pages/compiler/CompilerPage';
import { EctdV4DraftPage } from '@/pages/EctdV4DraftPage';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';

// phase-fix-validation-banner-crash — wrap a route element in the app's
// full-page error boundary so a render crash inside the page degrades to an
// honest error card (message + Reload) instead of white-screening the route.
// (Sharon 2026-07-18: the Compiler route had no boundary; a banner crash took
// the whole route down.)
function withBoundary(label: string, node: ReactElement): ReactElement {
  return (
    <ErrorBoundary label={label} showReload>
      {node}
    </ErrorBoundary>
  );
}

// react-router-dom v7 data router. Login lives at `/` outside the shell;
// the rest of the app sits under `<RequireAuth><AppShell />`. RequireAuth
// is a pass-through stub in Phase 4.2 — real MSAL gating arrives in 4.3.
export const router = createBrowserRouter([
  { path: '/', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  // D-25 — the invitation acceptance page lives OUTSIDE the authenticated
  // shell: an invited person arrives here before they have any role, and the
  // page itself handles the signed-out state. Wrapped in the app's error
  // boundary so a render fault degrades to an honest card.
  { path: '/join', element: withBoundary('Join', <JoinPage />), errorElement: <RouteErrorPage /> },
  {
    path: '/',
    errorElement: <RouteErrorPage />,
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: 'home', element: <HomePage /> },
      // Calendar is paused; existing bookmarks return to the dashboard.
      { path: 'calendar', element: <Navigate to="/home" replace /> },
      // Phase 5.X-notifications-system — full notifications page, linked
      // from the bell-icon dropdown "View all" footer.
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'submissions/new', element: <NewSubmissionPage /> },
      // Phase 5.25 — top-level Import workflow (creates submissions
      // from imported eCTD packages or FDA ESG history). NOT scoped to
      // an existing submission. UI shell shipped this phase; backend
      // endpoints split into Phases 5.25.1 (XML), 5.25.2 (ZIP),
      // 5.25.3 (openFDA). Matches V1's `/import` route.
      { path: 'import', element: <ImportPage /> },
      { path: 'submissions/:id', element: withBoundary('Submission', <SubmissionDetailPage />) },
      { path: 'submissions/:id/ectd4-draft', element: withBoundary('eCTD 4.0 draft', <EctdV4DraftPage />) },
      { path: 'submissions/:id/upload', element: <UploadDocsPage /> },
      { path: 'submissions/:id/validate', element: <ValidationReportPage /> },
      // phase-compiler-retire-legacy — `submissions/:id/package` (PackagePage,
      // the legacy "Generate eCTD Package" screen) is retired; the URL now
      // falls through to the router's 404. Compile lives on the Compiler page.
      // Wave E.0 — Compiler / Review scaffold. The top-level /compiler
      // route is no longer in the navigation drawer (dfa05e4: submission-
      // scoped, a dead end from the menu) but stays registered for direct
      // links; the per-submission /submissions/:submissionId/compiler is the
      // real entry, reached from Submission Details.
      { path: 'compiler', element: withBoundary('Compiler', <CompilerPage />) },
      {
        path: 'submissions/:submissionId/compiler',
        element: withBoundary('Compiler', <CompilerPage />),
      },
      { path: 'templates', element: <TemplatesListPage /> },
      { path: 'templates/:templateId', element: <TemplateDetailPage /> },
      // Phase 5.7 Wave D-1 — admin security pages. The permission gate
      // happens inside the page component (renders <PermissionDenied />
      // when the user lacks the required permission), not via a route
      // redirect, so deep-links surface a clear message instead of a 404.
      { path: 'admin/users', element: <UsersPage /> },
      { path: 'admin/roles', element: <RolesPage /> },
      { path: 'admin/audit-log', element: <AuditLogPage /> },
      // phase-a5 — admin Assistant Activity (usage + cost audit view).
      { path: 'admin/assistant-activity', element: <AssistantActivityPage /> },
      // Wave D-2.a — real pages for Company Profile + Validation Rules.
      { path: 'admin/company-profile', element: <CompanyProfilePage /> },
      { path: 'admin/validation-rules', element: <ValidationRulesPage /> },
      // Wave D-2.b — real pages for Form Templates + Prompt Library.
      { path: 'admin/form-templates', element: <FormTemplatesPage /> },
      { path: 'admin/prompt-library', element: <PromptLibraryPage /> },
      // Wave D-2.c — Spec Files (final ComingSoon replaced). Backend at
      // src/routes/spec-files.ts landed in Phase 5.14 (commit f378eab).
      { path: 'admin/spec-files', element: <SpecFilesPage /> },
      // "My Settings" — org / user / submission settings (Phase 5.9 backend,
      // 3-tier merge; editable since Phase 5.20b). Reached from the profile
      // menu's "My Settings" (hidden in local dev mode, where there is no
      // profile menu), Submission Details "⚙ Settings" (?submissionId=), the
      // Prompt Library threshold links and the System Health on-call hint.
      // No page-level gate BY DESIGN: every signed-in user owns their
      // user-level settings and notification preferences. Sections are gated
      // individually (T16-D6): org writes need Users:manage, submission writes
      // Submissions:write, and the assistant model catalogue renders only with
      // AuditLog:read (editable with Users:manage too).
      { path: 'admin/settings', element: <SettingsPage /> },
      // Phase 5.19.2 — operator-facing view of Phase 5.19's
      // /api/v1/health/detailed endpoint, which the backend gates on
      // AuditLog.read (T-10); the page gates on the same permission.
      { path: 'admin/system-health', element: <SystemHealthPage /> },
    ],
  },
  // Anything else: an honest "page not found" rather than react-router's
  // developer error screen.
  { path: '*', element: <RouteErrorPage missing /> },
]);
